import { type EventContentObjectDetail, type Program, ProgramInviteType } from 'models';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosErrorHandler, Modal, Skeleton, Space, message } from 'ui';
import { handleAxiosError } from 'common/error-handlers';
import type { EventContentObjectDataObject } from 'models/service';
import { useCreateProgramEvent, useEventQuery, useUpdateEvent } from '../../queries';
import { DataStepEventForm, type DefaultEventData } from './data-step-event-form';

type P = {
  eventId: string;
  isOpened: boolean;
  mode: 'duplicate' | 'edit';
  onCancel: () => void;
  onSuccess: (data: EventContentObjectDataObject) => Promise<void> | void;
  program?: Program;
};

const EditEventFormModal = ({ eventId, isOpened, mode, onCancel, onSuccess, program }: P) => {
  const { t } = useTranslation();

  const {
    data: eventContentObject,
    error: eventError,
    isFetching: isFetchingEvent,
    isLoading: isLoadingEvent,
  } = useEventQuery({ id: eventId, options: { throwOnError: false } });

  const { isPending: isUpdatingEvent, mutate: updateEvent } = useUpdateEvent();
  const { isPending: isDuplicatingEvent, mutate: duplicateEvent } = useCreateProgramEvent({
    mode: 'duplicate',
  });

  const [inviteType, setInviteType] = useState(ProgramInviteType.empty);

  const isInviteOpened = Boolean(inviteType);

  // TODO: refactor/move to one place, similar logic is in `events/hooks/useGetEventRoleName`
  const getTitleName = () => {
    const eventType = eventContentObject?.event.eventType;

    if (isInviteOpened) {
      switch (inviteType) {
        case ProgramInviteType.invitees:
          return 'Invite attendees';
        case ProgramInviteType.moderators:
          return 'Invite admins / hosts';
        case ProgramInviteType.presenters:
          return 'Invite presenters';
        case ProgramInviteType.empty:
          throw new Error(t('Unsupported option: {{option}}', { option: ProgramInviteType.empty }));
      }
    }

    if (mode === 'duplicate') {
      switch (eventType) {
        case 'conference':
          return 'Duplicate Video conference';
        case undefined:
          return 'Duplicate';
        case 'webinar':
          return 'Duplicate Webinar / Webcast';
      }
    }

    switch (eventType) {
      case 'conference':
        return 'Edit Video Conference';
      case undefined:
        return 'Edit';
      case 'webinar':
        return 'Edit Webinar / Webcast';
    }
  };

  const onSubmit = (data: DefaultEventData, loadedEventContentObject: EventContentObjectDetail) => {
    if (mode === 'duplicate') {
      duplicateEvent(
        { data },
        {
          onError(error) {
            handleAxiosError(error, 'Failed to duplicate event.');
          },
          async onSuccess(eventData) {
            message.success(t('Event was duplicated.'));
            await onSuccess(eventData.eventContentDataObject);
          },
        },
      );
    } else {
      updateEvent(
        { data, eventContentObject: loadedEventContentObject },
        {
          onError(error) {
            handleAxiosError(error, 'Failed to edit event.');
          },
          async onSuccess(eventData) {
            message.success(t('Event was updated.'));
            await onSuccess(eventData.eventContentDataObject);
          },
        },
      );
    }
  };

  return (
    <Modal
      className="edit-event-form-modal"
      closable={!isInviteOpened}
      isOpened={isOpened}
      onCancel={onCancel}
      title={getTitleName()}
      width="medium"
    >
      <Modal.HugContent>
        <AxiosErrorHandler actions={false} error={eventError}>
          {(isLoadingEvent && isFetchingEvent) || !eventContentObject ? (
            <Space className="edit-event-form-modal__skeleton" orientation="vertical" size="large">
              <Skeleton active paragraph={{ rows: 8 }} />
              <Skeleton active paragraph={{ rows: 14 }} />
              <Skeleton active paragraph={{ rows: 12 }} />
              <Skeleton active paragraph={{ rows: 10 }} />
              <Skeleton active paragraph={{ rows: 8 }} />
            </Space>
          ) : (
            <DataStepEventForm
              eventContentObject={eventContentObject}
              eventType={eventContentObject.event.eventType}
              inviteType={inviteType}
              isLoading={isUpdatingEvent || isDuplicatingEvent}
              mode={mode}
              onCancel={onCancel}
              onInviteesTypeChanged={setInviteType}
              onSubmit={(data) => {
                onSubmit(data, eventContentObject);
              }}
              program={program}
            />
          )}
        </AxiosErrorHandler>
      </Modal.HugContent>
    </Modal>
  );
};

export default EditEventFormModal;
